import React, { createContext, useContext, useState } from 'react';
import type { Score, Category } from '../types';
import type { HarmonicScore } from '../types/harmonic';
import { characteristics } from '../data/characteristics';
import { harmonicLevels } from '../data/harmonicLevels';

interface AssessmentContextType {
  scores: Score[] | undefined;
  setScores: (scores: Score[]) => void;
  harmonicScores: HarmonicScore[] | undefined;
  setHarmonicScores: (scores: HarmonicScore[]) => void;
  formatDataForAI: (prompt: string) => string;
}

const AssessmentContext = createContext<AssessmentContextType | undefined>(undefined);

export function AssessmentProvider({ children }: { children: React.ReactNode }) {
  const [scores, setScores] = useState<Score[]>();
  const [harmonicScores, setHarmonicScores] = useState<HarmonicScore[]>();

  const formatDataForAI = (prompt: string) => {
    if (!scores || !harmonicScores) {
      return prompt;
    }

    const categories: Category[] = ['being', 'doing', 'having'];
    const categoryLabels: Record<Category, string> = {
      being: 'BEING',
      doing: 'DOING',
      having: 'HAVING',
    };

    const groupedDetails = categories.map(cat => {
      const charsInCategory = characteristics.filter(c => c.category === cat);
      const lines = charsInCategory.map(char => {
        const score = scores.find(s => s.characteristicId === char.id);
        return `  - ${char.name}: ${Math.round(score?.normalized ?? 0)}%`;
      }).join('\n');
      return `${categoryLabels[cat]}\n${lines}`;
    }).join('\n\n');

    // Get dominant harmonic level
    const dominantHarmonic = harmonicScores.reduce((prev, current) =>
      current.normalized > prev.normalized ? current : prev
    );
    const dominantLevel = harmonicLevels.find(level => level.id === dominantHarmonic.levelId);

    // Format all harmonic scores
    const harmonicDetails = harmonicScores
      .sort((a, b) => b.normalized - a.normalized)
      .map(score => {
        const level = harmonicLevels.find(l => l.id === score.levelId);
        return `${level?.name}: ${Math.round(score.normalized)}%`;
      }).join('\n');

    return `
ASSESSMENT RESULTS (DO NOT modify the categories, characteristic names, or scores below — use them exactly as provided):

Characteristics by Fixed Category:
${groupedDetails}

Harmonic Scale:
Dominant Level: ${dominantLevel?.name} (${Math.round(dominantHarmonic.normalized)}%)

Detailed Harmonic Levels:
${harmonicDetails}

IMPORTANT INSTRUCTIONS:
- The three categories (Being, Doing, Having) are FIXED. Each characteristic is already assigned to its correct category. Do NOT reassign, move, or reorganize any characteristic to a different category.
- Use the exact scores shown above. Do NOT estimate, round differently, or invent any scores.
- When referencing a characteristic, always place it under its assigned category shown above.

Based on these results, ${prompt}
`;
  };

  return (
    <AssessmentContext.Provider value={{
      scores,
      setScores,
      harmonicScores,
      setHarmonicScores,
      formatDataForAI
    }}>
      {children}
    </AssessmentContext.Provider>
  );
}

export function useAssessment() {
  const context = useContext(AssessmentContext);
  if (context === undefined) {
    throw new Error('useAssessment must be used within an AssessmentProvider');
  }
  return context;
}